"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import Script from "next/script";
import { usePathname } from "next/navigation";
import {
  identifyAnalytics,
  initAnalyticsClients,
  resetAnalytics,
  setAnalyticsUtm,
  trackAnalytics,
} from "@/lib/analytics/client";
import { AnalyticsEvent } from "@/lib/analytics/events";
import type {
  AnalyticsProperties,
  AnalyticsUserTraits,
} from "@/lib/analytics/types";
import { useAuth } from "@/lib/context/AuthProvider";
import { useUtm } from "@/lib/context/UtmProvider";

interface AnalyticsContextValue {
  track: (name: string, properties?: AnalyticsProperties) => string;
  identify: (userId: string | null, traits?: AnalyticsUserTraits) => void;
  reset: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

const PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID?.trim() ?? "";

function emailFromUser(user: {
  identities?: { identifier: string | null; provider: string }[];
} | null): string | null {
  if (!user?.identities?.length) return null;
  for (const identity of user.identities) {
    const id = identity.identifier?.trim();
    if (id && id.includes("@")) return id;
  }
  return null;
}

function FacebookPixelScript() {
  if (!PIXEL_ID) return null;

  return (
    <Script
      id="meta-pixel"
      // Defer past LCP — Pixel is ~175 KiB and was contending with first paint.
      strategy="lazyOnload"
      onLoad={() => {
        initAnalyticsClients();
      }}
      dangerouslySetInnerHTML={{
        // Load stub + fbevents.js only. `fbq('init')` runs once in facebook.client.
        __html: `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
        `,
      }}
    />
  );
}

function AnalyticsBootstrap({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { utm } = useUtm();
  const { user, isAuthenticated, status } = useAuth();

  useEffect(() => {
    initAnalyticsClients();
  }, []);

  useEffect(() => {
    setAnalyticsUtm(utm);
  }, [utm]);

  useEffect(() => {
    if (status === "checking") return;
    if (isAuthenticated && user) {
      identifyAnalytics(String(user.id), {
        email: emailFromUser(user),
      });
    }
  }, [isAuthenticated, status, user]);

  useEffect(() => {
    if (!pathname) return;
    trackAnalytics(AnalyticsEvent.PageView, {
      path: pathname,
      title: typeof document !== "undefined" ? document.title : undefined,
    });
  }, [pathname]);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      track: trackAnalytics,
      identify: identifyAnalytics,
      reset: resetAnalytics,
    }),
    [],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      <FacebookPixelScript />
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * Must sit under AuthProvider + UtmProvider.
 * Loads Meta Pixel, inits Amplitude, tracks page views, identifies signed-in users.
 */
export function AnalyticsProvider({ children }: { children: ReactNode }) {
  return <AnalyticsBootstrap>{children}</AnalyticsBootstrap>;
}

export function useAnalytics(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error("useAnalytics must be used within <AnalyticsProvider>");
  }
  return ctx;
}
